<template>
	<div class="m-code-batch">
		<c-input
			ref="codeInput"
			:name="name"
			type="textarea"
			:height-resize="false"
			:placeholder="placeholder"
			@input="tipHandle"
		></c-input>
		<div v-if="tip" class="m-code-batch-tip" :class="{'z-warn': warn}">
			<span class="m-code-batch-tip-text">{{tip}}</span>
			<a v-if="clearable" class="m-code-batch-clear" @mousedown="preventBlur" @click="clear(true)">清空</a>
		</div>
	</div>
</template>

<script>
	/**
	 * 码类批量输入框（背贴码、IMEI等）
	 * 支持一次粘贴多个码：粘贴时自动按分隔符清洗并重写为一行一个码，失焦时兜底再格式化一次；
	 * 搜索栏每个条件项是固定32px高的网格，所以常态只露出开头内容、与相邻输入框完全等高，
	 * 聚焦时原地浮出面板展开全部内容与识别结果提示，不撑开搜索栏；
	 * 查询条件以多行文本（字符串）提交，参数名由name指定，约定为复数形式，
	 * 服务端按同一套分隔规则解析后再分流：单个码走原有模糊查询，多个码走精确IN查询。
	 * 码值保持粘贴原样，不做大小写转换。
	 * */
	export default {
		name: 'mCodeBatchInput',

		props: {
			//查询参数名，约定使用复数形式，如produce_unique_codes
			name: String,
			//字段名称，用于提示文案
			fieldName: {
				type: String,
				default: '码'
			},
			placeholder: {
				type: String,
				default: '支持批量粘贴'
			},
			//数量上限，与服务端BatchCodeUtil.CODE_MAX_SIZE保持一致
			max: {
				type: Number,
				default: 200
			},
			//是否在识别结果提示右侧显示文字版清空入口
			clearable: {
				type: Boolean,
				default: true
			}
		},

		data() {
			return {
				tip: '',
				warn: false
			}
		},

		mounted() {
			this.codeInput = this.$refs.codeInput;
			this.$input = this.$el.querySelector('textarea') || this.$el.querySelector('input');
			if (!this.$input) return;

			this.$input.addEventListener('paste', this.pasteHandle);
			this.$input.addEventListener('blur', this.format);
			this.$input.addEventListener('focus', this.tipHandle);
			this.$input.addEventListener('input', this.tipHandle);
			//在捕获阶段拦下回车，不干预多行框内的正常换行
			this.$el.addEventListener('keydown', this.enterHandle, true);
			this.$el.addEventListener('keyup', this.enterHandle, true);
		},

		beforeDestroy() {
			if (!this.$input) return;
			this.$input.removeEventListener('paste', this.pasteHandle);
			this.$input.removeEventListener('blur', this.format);
			this.$input.removeEventListener('focus', this.tipHandle);
			this.$input.removeEventListener('input', this.tipHandle);
			this.$el.removeEventListener('keydown', this.enterHandle, true);
			this.$el.removeEventListener('keyup', this.enterHandle, true);
		},

		methods: {
			/*********粘贴时格式化为规范码列表**********/
			pasteHandle(e) {
				let text = e.clipboardData && e.clipboardData.getData('text/plain');
				if (!text || !text.trim()) return;

				let codes = this.parse(text);
				//粘贴内容是单个不带分隔符的码时不改写，保持与原单行输入一致的手感（含扫码枪录入）
				if (codes.length <= 1 && !/[,，;；、\s]/.test(text.trim())) {
					this.$nextTick(this.tipHandle);
					return;
				}
				e.preventDefault();
				this.write(codes.join(','));
			},

			/*********失焦兜底格式化（手敲、修改过的内容）**********/
			format() {
				let value = this.read();
				if (!value.trim()) {
					this.tipHandle();
					return;
				}
				this.write(this.parse(value).join(','));
			},

			/*********多行输入框内回车只换行，不触发查询表单的回车自动查询***/
			enterHandle(e) {
				if (e.key === 'Enter' || e.keyCode === 13) {
					e.stopPropagation();
				}
			},

			/*********读取输入框当前文本**********/
			read() {
				return this.codeInput ? this.codeInput.get() : '';
			},

			/*********回写输入框，取值回值都走输入框组件自身，与扫码录入的回写方式一致**********/
			write(value) {
				if (!this.codeInput) return;
				if (this.read() === value) {
					this.tipHandle();
					return;
				}
				this.codeInput.set(value);
				this.codeInput.$emit('change', value);
				this.tipHandle();
			},

			/*********解析码列表：切分、清洗、去重（保序）**********/
			parse(text) {
				let list = (text || '').replace(/[\u00a0\ufeff\u200b]/g, '').split(/[,，;；、\s]+/);
				let codes = [];
				list.forEach(item => {
					item = this.clean(item);
					if (item && codes.indexOf(item) == -1) {
						codes.push(item);
					}
				});
				return codes;
			},

			/*********清洗单个码：去首尾空白与包裹引号，不改动码内部字符**********/
			clean(code) {
				let value = (code || '').trim();
				while (value.length > 1
					&& ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'")))) {
					value = value.substring(1, value.length - 1).trim();
				}
				return value;
			},

			/*********供外部主动清空（如重置单个条件），清空后保留焦点便于重新粘贴**********/
			clear(keepFocus) {
				this.write('');
				if (keepFocus) {
					this.$input && this.$input.focus();
				}
			},

			/*********按下清空入口时阻止默认行为，避免输入框先失焦导致浮层收起、点击落空**********/
			preventBlur(e) {
				e.preventDefault();
			},

			/*********刷新识别结果提示**********/
			tipHandle() {
				let text = this.read();
				let total = this.parse(text).length;
				let dup = this.count(text) - total;
				let invalid = this.parse(text).filter(item => !/^[A-Za-z0-9_\-.]{1,30}$/.test(item)).length;

				//监听输入框组件的input事件，自带清除按钮、重置条件等不走原生DOM事件的路径也能同步提示
				this.warn = total > this.max;
				if (!total) {
					this.tip = '';
					return;
				}
				let tips = [`识别${total}条`];
				dup > 0 && tips.push(`去重${dup}条`);
				invalid > 0 && tips.push(`${invalid}条格式异常`);
				tips.push(this.warn ? `${this.fieldName}最多${this.max}条，请分批查询` : total == 1 ? '单个码按模糊匹配' : '多个码按完整码精确匹配');
				this.tip = tips.join(' · ');
			},

			/*********统计去重前的原始条数**********/
			count(text) {
				return (text || '').replace(/[\u00a0\ufeff\u200b]/g, '').split(/[,，;；、\s]+/)
					.filter(item => this.clean(item)).length;
			}
		}
	}
</script>

<style lang="stylus">
	.m-code-batch {
		position: relative;
		width: 100%;

		//常态：与搜索栏其余输入框等高，只显示开头内容，不撑开固定32px高的搜索栏
		.c-input {
			height: 32px;
			border: none;
			background: none;
		}

		textarea {
			box-sizing: border-box;
			//输入框组件的自动撑高会写内联height，这里用important锁住行高，多余内容一律隐藏
			height: 32px !important;
			min-height: 32px;
			max-height: 32px !important;
			line-height: 20px;
			font-size: 12px;
			white-space: pre;
			overflow: hidden;
			resize: none;
		}
	}

	.m-code-batch-tip {
		display: none;
		font-size: 10px;
		line-height: 1.4;
		padding-top: 4px;
		color: #999;
		word-break: break-all;

		&.z-warn {
			color: #e53e3e;
		}
	}

	.m-code-batch-tip-text {
		flex-grow: 1;
	}

	//文字版清空入口，固定在提示文案最右侧；属次要操作，取色与输入框自带的清除图标一致，中性灰常态、悬停加深
	.m-code-batch-clear {
		flex-shrink: 0;
		margin-left: 8px;
		font-size: 12px;
		line-height: 1.2;
		color: #999;
		cursor: pointer;
		white-space: nowrap;
		transition: color .1s ease;

		&:hover {
			color: #333;
		}
	}

	//聚焦：原地浮出面板，脱离搜索栏文档流，不挤压tab栏与按钮行
	.m-code-batch:focus-within {
		position: absolute;
		width: 360px;
		max-width: 90vw;
		padding: 6px;
		border-radius: 2px;
		background: #fff;
		box-shadow: 0 4px 12px rgba(0, 0, 0, .18);
		z-index: 100;

		.c-input {
			height: auto;
			position: relative;

			//展开后面板变高，清除图标钉到右上角，避免随高分居中飘在中部
			.c-input-clear {
				position: absolute;
				right: 4px;
				top: 4px;
			}
		}

		textarea {
			height: 132px !important;
			min-height: 132px;
			max-height: 132px !important;
			line-height: 1.6;
			white-space: pre-wrap;
			overflow: auto;
		}

		.m-code-batch-tip {
			display: flex;
			align-items: flex-start;
		}
	}
</style>
